/**
 * GPS via @capacitor/geolocation (native)
 * Web fallback: navigator.geolocation
 */
import { Geolocation } from '@capacitor/geolocation';
import { isNative } from './platform.js';

export async function getCurrentCoordinates(options = {}) {
  const timeout = options.timeout ?? 15000;
  const enableHighAccuracy = options.enableHighAccuracy ?? true;

  if (isNative()) {
    const permission = await Geolocation.checkPermissions();
    if (permission.location !== 'granted') {
      const requested = await Geolocation.requestPermissions();
      if (requested.location !== 'granted') {
        throw new Error('Chưa cấp quyền vị trí cho ứng dụng');
      }
    }

    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy,
      timeout,
    });

    return normalize(position);
  }

  return getWebCoordinates({ timeout, enableHighAccuracy });
}

function getWebCoordinates({ timeout, enableHighAccuracy }) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Trình duyệt không hỗ trợ Geolocation'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(normalize(position)),
      (error) => reject(new Error(error.message || 'Không lấy được GPS')),
      { enableHighAccuracy, timeout, maximumAge: 0 }
    );
  });
}

function normalize(position) {
  const coords = position.coords;
  return {
    latitude: Number(coords.latitude),
    longitude: Number(coords.longitude),
    accuracy: coords.accuracy != null ? Number(coords.accuracy) : null,
    altitude: coords.altitude != null ? Number(coords.altitude) : null,
    capturedAt: new Date(position.timestamp || Date.now()).toISOString(),
    source: isNative() ? 'capacitor-geolocation' : 'browser-geolocation',
  };
}

export function formatCoordinates(value) {
  if (!value || value.latitude == null || value.longitude == null) return '';
  const lat = Number(value.latitude).toFixed(6);
  const lng = Number(value.longitude).toFixed(6);
  const acc = value.accuracy != null ? ` (±${Math.round(value.accuracy)}m)` : '';
  return `${lat}, ${lng}${acc}`;
}
